import { Box, Stack, Typography } from '@evaneos/b2b-ui';
import { compareAsc, isSameDay, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ERROR, SUCCESS } from '../../../../design-system/constants';
import { markMessageAsReadRequest } from '../../../../resources/discussion';
import {
    getPendingOrErrorSendingMessageIdsByDossierId,
    updateMessageStatus,
} from '../../../../resources/discussion/selectors';
import {
    DiscussionItem,
    Message as MessageType,
    ScheduledMessage as ScheduledMessageType,
} from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { useAuth } from '../../../../shared/providers/AuthProvider';
import { MessageTemplate } from '../../../../resources/message-template/types';
import {
    useFilteredDiscussionGroupedByDate,
    useLastTravellerMessage,
} from '../../../../resources/discussion/queries';
import { useGetAgentByDossierId } from '../../../../resources/dossier/queries';
import { getToday } from '../../../../shared/utilities/date';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';

import { useAttachmentManagement } from '../hooks/useAttachmentManagement';
import { useAT1Proposal } from '../hooks/useAT1Proposal';
import { useDiscussionAutoScroll } from '../hooks/useDiscussionAutoScroll';

import { AT1ProposalBanner } from './AT1ProposalBanner';
import { Message } from './Message';
import { MessageTemplatesPanel } from './MessageTemplatesPanel';
import { PhoneCallCard } from './PhoneCallCard';
import { ScheduledMessage } from './ScheduledMessage';
import { SendingMessage } from './SendingMessage';
import { EditMessageBox } from './message-box/EditMessageBox';
import { NewMessageBox } from './message-box/NewMessageBox';

import s from './Discussion.module.css';

interface Props {
    dossierId: DossierId;
    isFromNotification: boolean;
    targetPhoneCallId: number;
}

/* eslint-disable-next-line max-lines-per-function -- message rendering helpers and discussion lifecycle co-located intentionally */
export const DiscussionTab = ({ dossierId, isFromNotification, targetPhoneCallId }: Props) => {
    const { formatMessage, formatDate } = useIntl();
    const dispatch = useAppDispatch();
    const { isImpersonated, userId } = useAuth();

    const {
        messages,
        isResponseRequiredState,
        scheduledMessage,
        status: discussionStatus,
    } = useFilteredDiscussionGroupedByDate(dossierId);
    const lastTravellerMessage = useLastTravellerMessage(dossierId);

    const sendingMessageIds = useAppSelector(
        getPendingOrErrorSendingMessageIdsByDossierId(dossierId),
    );
    const editingMessageStatus = useAppSelector(updateMessageStatus(dossierId));

    const {
        allAttachments,
        getAttachments: getAttachmentsForMessage,
        addAttachment,
        removeAttachment,
    } = useAttachmentManagement(sendingMessageIds);

    const assignedAgent = useGetAgentByDossierId(dossierId);
    const isOwner = assignedAgent && assignedAgent.id === userId?.toString();

    // A dossier is "empty" when it has no sent, sending, or scheduled messages.
    // !scheduledMessage is intentional: a dossier with only a scheduled message should not show
    // the proposal banner since the agent has already composed a first message.
    const hasNoMessageInTheDiscussion =
        Object.keys(messages || {}).length === 0 &&
        sendingMessageIds.length === 0 &&
        !scheduledMessage;

    const {
        proposalStatus,
        at1ProposalData,
        participants,
        showAT1ProposalBanner,
        handleGenerateAT1ProposalClick,
        onAT1ProposalConsumed,
    } = useAT1Proposal({ dossierId, hasNoMessageInTheDiscussion });

    const discussionRef = useRef<HTMLDivElement>(null);
    const { scrollToBottom } = useDiscussionAutoScroll(discussionRef, messages, sendingMessageIds);

    const [displayMessageTemplatesPanel, setDisplayMessageTemplatesPanel] = useState(false);
    const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<MessageTemplate | null>(
        null,
    );
    const [editingMessage, setEditingMessage] = useState<
        MessageType | ScheduledMessageType | null | undefined
    >(null);

    const [prevEditingStatus, setPrevEditingStatus] = useState(editingMessageStatus);
    if (prevEditingStatus !== editingMessageStatus) {
        setPrevEditingStatus(editingMessageStatus);
        if (editingMessageStatus === SUCCESS || editingMessageStatus === ERROR) {
            setEditingMessage(null);
        }
    }

    useEffect(() => {
        if (
            discussionStatus === SUCCESS &&
            !isImpersonated &&
            isOwner &&
            lastTravellerMessage?.id
        ) {
            dispatch(
                markMessageAsReadRequest(dossierId, parseInt(lastTravellerMessage.id.toString())),
            );
        }
    }, [dispatch, lastTravellerMessage, discussionStatus, isImpersonated, isOwner, dossierId]);

    function renderDate(date: Date): JSX.Element {
        const displayedDate = isSameDay(getToday(), date)
            ? formatMessage({ id: 'global.today' })
            : formatDate(date, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
              });
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: { xs: 3, md: 4 },
                    marginBottom: { xs: 1, md: 2 },
                }}
                data-testid="message-group-date"
            >
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    {displayedDate}
                </Typography>
            </Box>
        );
    }

    function getSpacingForItem(
        currentItem: DiscussionItem,
        nextItem: DiscussionItem | undefined,
    ): number {
        if (!nextItem) return 0;

        const isCurrentPhoneCall = !('authorId' in currentItem);
        const isNextPhoneCall = !('authorId' in nextItem);

        if (isCurrentPhoneCall || isNextPhoneCall) return 1;

        const isCurrentFromAgent = currentItem.isFromAgent;
        const isNextFromAgent = nextItem.isFromAgent;

        return isCurrentFromAgent === isNextFromAgent ? 1 : 3;
    }

    function renderMessages(discussionItems: DiscussionItem[]) {
        return discussionItems.map((discussionItem, index) => {
            const nextItem = discussionItems[index + 1];
            const spacing = getSpacingForItem(discussionItem, nextItem);

            if ('authorId' in discussionItem) {
                return (
                    <Box key={discussionItem.id} marginBottom={spacing}>
                        <Message
                            message={discussionItem}
                            isResponseRequiredState={isResponseRequiredState}
                            onEdit={() => {
                                setEditingMessage(discussionItem);
                                // eslint-disable-next-line sonarjs/no-nested-functions -- nested callbacks required by the setTimeout API
                                setTimeout(() => scrollToBottom(), 0);
                            }}
                            isBlurred={
                                editingMessage != null && editingMessage.id !== discussionItem.id
                            }
                            isEditing={
                                editingMessage != null && editingMessage.id === discussionItem.id
                            }
                        />
                    </Box>
                );
            }

            return (
                <Box key={discussionItem.id} marginBottom={spacing}>
                    <PhoneCallCard
                        discussionPhoneCall={discussionItem}
                        shouldAutoOpen={
                            isFromNotification && targetPhoneCallId === discussionItem.id
                        }
                    />
                </Box>
            );
        });
    }

    function handleMessageTemplatesPanel(): void {
        setDisplayMessageTemplatesPanel(!displayMessageTemplatesPanel);
    }

    return (
        <>
            <Stack
                ref={discussionRef}
                className={s.discussion}
                padding={{ xs: 2, md: 3 }}
                paddingTop={0}
                data-testid="discussion"
            >
                {messages &&
                    Object.keys(messages)
                        .sort((a, b) => compareAsc(parseISO(a), parseISO(b)))
                        .map((date) => (
                            <React.Fragment key={date}>
                                {renderDate(messages[date][0].sendDate)}
                                <Stack>{renderMessages(messages[date])}</Stack>
                            </React.Fragment>
                        ))}
                {sendingMessageIds.map((sendingMessageId) => (
                    <SendingMessage
                        key={sendingMessageId}
                        sendingMessageId={sendingMessageId}
                        attachments={allAttachments[sendingMessageId] || {}}
                    />
                ))}
                {scheduledMessage && (
                    <ScheduledMessage
                        data-testid="scheduled-messages-preview"
                        key={scheduledMessage.id}
                        scheduledMessage={scheduledMessage}
                        onEdit={() => {
                            setEditingMessage(scheduledMessage);
                            setTimeout(() => scrollToBottom(), 0);
                        }}
                        isBlurred={
                            editingMessage != null && editingMessage.id !== scheduledMessage.id
                        }
                        isEditing={
                            editingMessage != null && editingMessage.id === scheduledMessage.id
                        }
                    />
                )}
                {showAT1ProposalBanner && participants && (
                    <Box sx={{ marginTop: 'auto' }}>
                        <AT1ProposalBanner
                            participants={participants}
                            status={proposalStatus}
                            onClick={handleGenerateAT1ProposalClick}
                        />
                    </Box>
                )}
            </Stack>

            {editingMessage ? (
                <EditMessageBox
                    dossierId={dossierId}
                    editingMessage={editingMessage}
                    onCancel={() => setEditingMessage(null)}
                    onHeightChange={scrollToBottom}
                />
            ) : (
                <NewMessageBox
                    onHeightChange={scrollToBottom}
                    handleMessageTemplatesPanel={handleMessageTemplatesPanel}
                    selectedMessageTemplate={selectedMessageTemplate}
                    removeSelectedMessageTemplate={() => setSelectedMessageTemplate(null)}
                    disableAutoFocus={isFromNotification}
                    getAttachments={getAttachmentsForMessage}
                    addAttachment={addAttachment}
                    removeAttachment={removeAttachment}
                    proposalStatus={proposalStatus}
                    at1ProposalData={at1ProposalData}
                    onAT1ProposalConsumed={onAT1ProposalConsumed}
                />
            )}

            <MessageTemplatesPanel
                dossierId={dossierId}
                handleMessageTemplatesPanel={handleMessageTemplatesPanel}
                displayPanel={displayMessageTemplatesPanel}
                applyMessageTemplate={setSelectedMessageTemplate}
            />
        </>
    );
};
